import React from "react";
import { FaPencilAlt, FaTrashAlt, FaEye } from "react-icons/fa";
import { BsPersonFillGear } from "react-icons/bs";
import { RiFileEditFill } from "react-icons/ri";
import { ImExit } from "react-icons/im";
import { Assistant } from "../../../api/models";
import { ButtonTooltip } from "../../ButtonTooltip";
import { LoadingDots } from "../../LoadingDots/LoadingDots";
import { MdAdminPanelSettings } from "react-icons/md";

interface Props {
    data: Assistant[];
    isShared?: boolean;
    userGroups?: string[];
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => void;
    onView?: (id: string) => void;
    onLeave?: (id: string) => void;
    onAdmin?: (id: string) => void;
    onFileManager?: (id: string) => void;
    currentUserId: string;
    isLoading?: boolean;
}

export const AssistantTable = ({
    data,
    isShared = false,
    userGroups = [],
    onEdit,
    onDelete,
    onView,
    onLeave,
    onAdmin,
    onFileManager,
    currentUserId,
    isLoading
}: Props) => {
    return (
        <table className="w-full">
            <thead>
                <tr className="border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wide" style={{ width: "35%" }}>
                        Title
                    </th>
                    <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wide" style={{ width: "25%" }}>
                        Date Created
                    </th>
                    <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wide" style={{ width: "25%" }}>
                        Owner
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wide" style={{ width: "15%" }}>
                        Actions
                    </th>
                </tr>
            </thead>
            <tbody>
                {data.map(a => {
                    const isEquiom = a.type === "Equiom";
                    const isEllieHR = a.id === "3f4ded35-ad63-4b69-87a5-699d5101762b";
                    const isEllieGPT = a.id === "e4bd5df7-9ec0-484b-bccb-0c24691f4b22";

                    const isUser = a.userIds?.includes(currentUserId) || userGroups?.some(group => a.groups?.includes(group));
                    const isAdmin = a.adminIds?.includes(currentUserId) || userGroups?.some(group => a.adminGroups?.includes(group));
                    const isFileManager = a.fileManagerIds?.includes(currentUserId) || userGroups?.some(group => a.fileManagerGroups?.includes(group));

                    const showSharedActions = isShared && !isEquiom;

                    return (
                        <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-4 text-sm font-medium text-gray-900">{a.title}</td>
                            <td className="hidden md:table-cell px-4 py-4 text-sm text-gray-600">{new Date(a.created_at).toLocaleDateString()}</td>
                            <td className="hidden md:table-cell px-4 py-4 text-sm text-gray-600">{isEquiom ? "Equiom" : a.user_display_name || "Unknown"}</td>
                            <td className="px-4 py-4">
                                {isShared && !isEllieHR && !isEllieGPT ? (
                                    showSharedActions ? (
                                        <div className="flex justify-end gap-2">
                                            {onFileManager && isFileManager && (
                                                <ButtonTooltip
                                                    title={
                                                        a.available ? (
                                                            "Manage Files"
                                                        ) : (
                                                            <>
                                                                Assistant is learning
                                                                <LoadingDots />
                                                            </>
                                                        )
                                                    }
                                                    placement="top"
                                                    disableHoverListener={!a.available}
                                                    disableFocusListener={!a.available}
                                                    disableTouchListener={!a.available}
                                                >
                                                    <span>
                                                        <button
                                                            disabled={!a.available}
                                                            onClick={a.available && onFileManager ? () => onFileManager(a.id) : undefined}
                                                            className={
                                                                a.available
                                                                    ? "p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                                                                    : "p-2 text-gray-300 cursor-not-allowed rounded-md"
                                                            }
                                                            aria-label="Manage Files"
                                                        >
                                                            <RiFileEditFill className="w-4 h-4" />
                                                        </button>
                                                    </span>
                                                </ButtonTooltip>
                                            )}
                                            {onAdmin && isAdmin && (
                                                <ButtonTooltip
                                                    title={
                                                        a.available ? (
                                                            "Admin Settings"
                                                        ) : (
                                                            <>
                                                                Assistant is learning
                                                                <LoadingDots />
                                                            </>
                                                        )
                                                    }
                                                    placement="top"
                                                    disableHoverListener={!a.available}
                                                    disableFocusListener={!a.available}
                                                    disableTouchListener={!a.available}
                                                >
                                                    <span>
                                                        <button
                                                            disabled={!a.available}
                                                            onClick={a.available && onAdmin ? () => onAdmin(a.id) : undefined}
                                                            className={
                                                                a.available
                                                                    ? "p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-md transition-colors"
                                                                    : "p-2 text-gray-300 cursor-not-allowed rounded-md"
                                                            }
                                                            aria-label="Admin Settings"
                                                        >
                                                            <MdAdminPanelSettings className="w-5 h-5" />
                                                        </button>
                                                    </span>
                                                </ButtonTooltip>
                                            )}
                                            {onView && (
                                                <ButtonTooltip title="View Assistant" placement="top">
                                                    <span>
                                                        <button
                                                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                                                            onClick={() => onView(a.id)}
                                                            aria-label="View"
                                                        >
                                                            <FaEye className="w-4 h-4" />
                                                        </button>
                                                    </span>
                                                </ButtonTooltip>
                                            )}
                                        </div>
                                    ) : null
                                ) : (
                                    <div className="flex items-center justify-end gap-1">
                                        {isLoading ? (
                                            <>
                                                {[1, 2, 3].map(i => (
                                                    <div
                                                        key={i}
                                                        className="w-8 h-8 rounded-md bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-shimmer"
                                                    />
                                                ))}
                                            </>
                                        ) : (
                                            <>
                                                {onFileManager && isFileManager && (
                                                    <ButtonTooltip
                                                        title={
                                                            a.available ? (
                                                                "Manage Files"
                                                            ) : (
                                                                <>
                                                                    Assistant is learning
                                                                    <LoadingDots />
                                                                </>
                                                            )
                                                        }
                                                        placement="top"
                                                        disableHoverListener={!a.available}
                                                        disableFocusListener={!a.available}
                                                        disableTouchListener={!a.available}
                                                    >
                                                        <span>
                                                            <button
                                                                disabled={!a.available}
                                                                onClick={a.available && onFileManager ? () => onFileManager(a.id) : undefined}
                                                                className={
                                                                    a.available
                                                                        ? "p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                                                                        : "p-2 text-gray-300 cursor-not-allowed rounded-md"
                                                                }
                                                                aria-label="Manage Files"
                                                            >
                                                                <RiFileEditFill className="w-4 h-4" />
                                                            </button>
                                                        </span>
                                                    </ButtonTooltip>
                                                )}
                                                {onAdmin && isAdmin && (
                                                    <ButtonTooltip
                                                        title={
                                                            a.available ? (
                                                                "Admin Settings"
                                                            ) : (
                                                                <>
                                                                    Assistant is learning
                                                                    <LoadingDots />
                                                                </>
                                                            )
                                                        }
                                                        placement="top"
                                                        disableHoverListener={!a.available}
                                                        disableFocusListener={!a.available}
                                                        disableTouchListener={!a.available}
                                                    >
                                                        <span>
                                                            <button
                                                                disabled={!a.available}
                                                                onClick={a.available && onAdmin ? () => onAdmin(a.id) : undefined}
                                                                className={
                                                                    a.available
                                                                        ? "p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-md transition-colors"
                                                                        : "p-2 text-gray-300 cursor-not-allowed rounded-md"
                                                                }
                                                                aria-label="Admin Settings"
                                                            >
                                                                <MdAdminPanelSettings className="w-5 h-5" />
                                                            </button>
                                                        </span>
                                                    </ButtonTooltip>
                                                )}
                                                {onDelete && (
                                                    <ButtonTooltip
                                                        title={
                                                            a.available ? (
                                                                "Delete Assistant"
                                                            ) : (
                                                                <>
                                                                    Assistant is learning
                                                                    <LoadingDots />
                                                                </>
                                                            )
                                                        }
                                                        placement="top"
                                                        disableHoverListener={!a.available}
                                                        disableFocusListener={!a.available}
                                                        disableTouchListener={!a.available}
                                                    >
                                                        <span>
                                                            <button
                                                                disabled={!a.available}
                                                                onClick={a.available && onDelete ? () => onDelete(a.id) : undefined}
                                                                className={
                                                                    a.available
                                                                        ? "p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                                                                        : "p-2 text-gray-300 cursor-not-allowed rounded-md"
                                                                }
                                                                aria-label="Delete"
                                                            >
                                                                <FaTrashAlt className="w-4 h-4" />
                                                            </button>
                                                        </span>
                                                    </ButtonTooltip>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};
